/**
 * Enhanced service for managing tee time data across multiple golf courses
 * In a production environment, this would interface with a backend API
 */
import { v4 as uuidv4 } from 'uuid';
import { TeeTime, TeeTimeSpot, DailyTeeTimeSchedule, Booking, Player, WeatherInfo } from '../types';
import { mockGolfCourses, getGolfCourseById } from './golfCourseService';

/**
 * Generates random weather data for a future date
 * @param date The date to generate weather for
 * @returns Weather information or undefined if date is too far in future
 */
const generateWeatherForecast = (date: Date): WeatherInfo | undefined => {
  const now = new Date();
  const daysDifference = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  // Only generate weather for dates up to 7 days in the future
  if (daysDifference > 7) {
    return undefined;
  }
  
  const conditions: Array<WeatherInfo['condition']> = [
    'Sunny', 'Partly Cloudy', 'Cloudy', 'Rainy', 'Stormy'
  ];
  
  const condition = conditions[Math.floor(Math.random() * conditions.length)];
  
  // Generate temperature based on condition
  let tempBase = 70; // Base temperature
  switch (condition) {
    case 'Sunny': tempBase += 10; break;
    case 'Partly Cloudy': tempBase += 5; break;
    case 'Cloudy': tempBase -= 3; break;
    case 'Rainy': tempBase -= 8; break;
    case 'Stormy': tempBase -= 12; break;
  }
  
  // Add some randomness to temperature
  const temperature = tempBase + Math.floor(Math.random() * 10) - 5;
  
  // Wind speed based on condition
  let windBase = 5;
  if (condition === 'Rainy' || condition === 'Stormy') {
    windBase = 15;
  }
  const windSpeed = windBase + Math.floor(Math.random() * 10);
  
  // Precipitation chance based on condition
  let precipitationChance = 0;
  switch (condition) {
    case 'Sunny': precipitationChance = Math.floor(Math.random() * 5); break;
    case 'Partly Cloudy': precipitationChance = 5 + Math.floor(Math.random() * 20); break;
    case 'Cloudy': precipitationChance = 25 + Math.floor(Math.random() * 25); break;
    case 'Rainy': precipitationChance = 70 + Math.floor(Math.random() * 30); break;
    case 'Stormy': precipitationChance = 85 + Math.floor(Math.random() * 15); break;
  }
  
  // Weather icon mapping
  const getIconForCondition = (cond: WeatherInfo['condition']): string => {
    switch (cond) {
      case 'Sunny': return 'sunny';
      case 'Partly Cloudy': return 'partly-cloudy';
      case 'Cloudy': return 'cloudy';
      case 'Rainy': return 'rainy';
      case 'Stormy': return 'stormy';
      default: return 'unknown';
    }
  };
  
  return {
    temperature,
    condition,
    windSpeed,
    precipitationChance,
    icon: getIconForCondition(condition)
  };
};

/**
 * Calculate tee time price based on course, day of week, and time of day
 * @param courseId The ID of the golf course
 * @param date The date for the tee time
 * @param hour The hour of the tee time (24-hour format)
 * @returns The calculated price
 */
const calculateTeeTimePrice = (courseId: string, date: Date, hour: number): number => {
  const course = getGolfCourseById(courseId);
  if (!course) return 100; // Default price if course not found
  
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const priceRange = isWeekend ? course.pricingRange.weekend : course.pricingRange.weekday;
  
  // Base price from the price range
  let basePrice = (priceRange.min + priceRange.max) / 2;
  
  // Adjust price based on time of day (peak hours cost more)
  if (hour >= 9 && hour <= 14) { // Peak hours (9 AM - 2 PM)
    basePrice += (priceRange.max - basePrice) * 0.5;
  } else if (hour < 8 || hour >= 16) { // Early morning or late afternoon
    basePrice -= (basePrice - priceRange.min) * 0.3;
  }
  
  // Round to nearest 5
  return Math.round(basePrice / 5) * 5;
};

/**
 * Determines if a tee time should have a special offer
 * @param date The date for the tee time
 * @param hour The hour of the tee time (24-hour format)
 * @returns Special offer text or undefined
 */
const getSpecialOffer = (date: Date, hour: number): string | undefined => {
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const now = new Date();
  const daysDifference = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  // Early bird special for early morning tee times
  if (hour < 8) {
    return 'Early Bird: Complimentary coffee and breakfast sandwich!';
  }
  
  // Twilight special for late afternoon
  if (hour >= 16) {
    return 'Twilight Special: 20% off cart rentals!';
  }
  
  // Last-minute booking discount
  if (daysDifference === 0) {
    return 'Last Minute: Save 15% on green fees!';
  }
  
  // Weekday warriors special
  if (!isWeekend && daysDifference <= 3) {
    return 'Weekday Warriors: Bring a friend for half price!';
  }
  
  // Most tee times won't have special offers
  // Only about 20% of tee times get random specials
  if (Math.random() < 0.2) {
    const specials = [
      'Group Discount: Save 10% when booking for 3+ players!',
      'Pro Shop Special: 15% off merchandise with your tee time!',
      'Golf & Dine: Free appetizer with lunch purchase after your round!'
    ];
    return specials[Math.floor(Math.random() * specials.length)];
  }
  
  return undefined;
};

/**
 * Generates a range of tee times for a specific date and course
 * @param date The date to generate tee times for
 * @param courseId The ID of the golf course
 * @param startHour The hour to start generating tee times (24-hour format)
 * @param endHour The hour to stop generating tee times (24-hour format)
 * @returns An array of tee times
 */
export const generateTeeTimes = (
  date: Date,
  courseId: string,
  startHour: number = 7, // Default start at 7 AM
  endHour: number = 19, // Default end at 7 PM
): TeeTime[] => {
  const teeTimes: TeeTime[] = [];
  
  // Clone the date to avoid modifying the original
  const teeTimeDate = new Date(date);
  
  // Set the time to the start hour
  teeTimeDate.setHours(startHour, 0, 0, 0);
  
  // Generate weather forecast for this date
  const weather = generateWeatherForecast(date);
  
  // Generate tee times at 15-minute intervals
  while (teeTimeDate.getHours() < endHour) {
    const currentHour = teeTimeDate.getHours();
    const startTime = `${currentHour.toString().padStart(2, '0')}:${teeTimeDate.getMinutes().toString().padStart(2, '0')}`;
    
    // Calculate end time (15 minutes later)
    const endTimeDate = new Date(teeTimeDate);
    endTimeDate.setMinutes(endTimeDate.getMinutes() + 15);
    const endTime = `${endTimeDate.getHours().toString().padStart(2, '0')}:${endTimeDate.getMinutes().toString().padStart(2, '0')}`;
    
    // Create 4 spots for this tee time
    const spots: TeeTimeSpot[] = Array(4).fill(null).map(() => ({
      id: uuidv4(),
      player: null,
      isBooked: false,
    }));
    
    // Calculate price for this tee time
    const price = calculateTeeTimePrice(courseId, date, currentHour);
    
    // Check for special offers
    const specialOffer = getSpecialOffer(date, currentHour);
    
    teeTimes.push({
      id: uuidv4(),
      date: new Date(teeTimeDate),
      startTime,
      endTime,
      courseId,
      spots,
      isFullyBooked: false,
      availableSpots: 4,
      price,
      specialOffer,
      weather,
    });
    
    // Move to the next 15-minute interval
    teeTimeDate.setMinutes(teeTimeDate.getMinutes() + 15);
  }
  
  return teeTimes;
};

/**
 * Generates a schedule of tee times for all courses for a week
 * @param startDate The starting date for the schedule
 * @param numberOfDays The number of days to generate (default: 7)
 * @returns An array of daily tee time schedules
 */
export const generateWeeklySchedule = (
  startDate: Date = new Date(),
  numberOfDays: number = 7
): DailyTeeTimeSchedule[] => {
  const schedule: DailyTeeTimeSchedule[] = [];
  
  for (let i = 0; i < numberOfDays; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() + i);
    
    // Generate schedules for each golf course
    mockGolfCourses.forEach(course => {
      schedule.push({
        date: new Date(currentDate),
        courseId: course.id,
        teeTimes: generateTeeTimes(currentDate, course.id),
      });
    });
  }
  
  return schedule;
};

// In-memory storage for bookings and tee times
let mockTeeTimes: DailyTeeTimeSchedule[] = [];
let mockBookings: Booking[] = [];

/**
 * Initialize mock data
 */
export const initializeMockData = (): void => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  mockTeeTimes = generateWeeklySchedule(today);
};

/**
 * Gets all tee times for a specific date and course
 * @param date The date to get tee times for
 * @param courseId The ID of the golf course
 * @returns The daily tee time schedule for the specified date and course
 */
export const getTeeTimesForDate = (date: Date, courseId: string): DailyTeeTimeSchedule | undefined => {
  if (mockTeeTimes.length === 0) {
    initializeMockData();
  }
  
  const dateStr = date.toDateString();
  return mockTeeTimes.find(schedule => 
    schedule.date.toDateString() === dateStr && schedule.courseId === courseId
  );
};

/**
 * Gets all tee times for all courses for a specific date
 * @param date The date to get tee times for
 * @returns An array of daily tee time schedules for all courses
 */
export const getAllTeeTimesForDate = (date: Date): DailyTeeTimeSchedule[] => {
  if (mockTeeTimes.length === 0) {
    initializeMockData();
  }
  
  const dateStr = date.toDateString();
  return mockTeeTimes.filter(schedule => schedule.date.toDateString() === dateStr);
};

/**
 * Gets all tee times for the next week for all courses
 * @returns An array of daily tee time schedules
 */
export const getWeeklySchedule = (): DailyTeeTimeSchedule[] => {
  if (mockTeeTimes.length === 0) {
    initializeMockData();
  }
  
  return mockTeeTimes;
};

/**
 * Gets all tee times for the next week for a specific course
 * @param courseId The ID of the golf course
 * @returns An array of daily tee time schedules for the specified course
 */
export const getCourseWeeklySchedule = (courseId: string): DailyTeeTimeSchedule[] => {
  if (mockTeeTimes.length === 0) {
    initializeMockData();
  }
  
  return mockTeeTimes.filter(schedule => schedule.courseId === courseId);
};

/**
 * Books a tee time for one or more players
 * @param courseId The ID of the golf course
 * @param teeTimeId The ID of the tee time to book
 * @param players Array of players to book the tee time for
 * @param additionalServices Optional additional services for the booking
 * @returns The booking confirmation or null if booking failed
 */
export const bookTeeTime = (
  courseId: string,
  teeTimeId: string, 
  players: Omit<Player, 'id'>[],
  additionalServices?: {
    caddies?: number;
    lessonRequested?: boolean;
    foodPreOrder?: boolean;
  }
): Booking | null => {
  if (players.length === 0 || players.length > 4) {
    return null; // Invalid number of players
  }
  
  // Find the tee time
  let targetTeeTime: TeeTime | undefined;
  let targetSchedule: DailyTeeTimeSchedule | undefined;
  
  for (const schedule of mockTeeTimes) {
    if (schedule.courseId !== courseId) continue;
    
    const teeTime = schedule.teeTimes.find(tt => tt.id === teeTimeId);
    if (teeTime) {
      targetTeeTime = teeTime;
      targetSchedule = schedule;
      break;
    }
  }
  
  if (!targetTeeTime || !targetSchedule) {
    return null; // Tee time not found
  }
  
  // Check if enough spots are available
  if (targetTeeTime.availableSpots < players.length) {
    return null; // Not enough spots available
  }
  
  // Create player objects with IDs
  const bookingPlayers: Player[] = players.map(player => ({
    ...player,
    id: uuidv4(),
  }));
  
  // Update the tee time spots
  let spotIndex = 0;
  for (let i = 0; i < targetTeeTime.spots.length && spotIndex < players.length; i++) {
    const spot = targetTeeTime.spots[i];
    if (!spot.isBooked) {
      spot.isBooked = true;
      spot.player = bookingPlayers[spotIndex];
      spotIndex++;
    }
  }
  
  // Update tee time availability
  targetTeeTime.availableSpots -= players.length;
  targetTeeTime.isFullyBooked = targetTeeTime.availableSpots === 0;
  
  // Calculate total price
  const totalPrice = targetTeeTime.price * players.length;
  
  // Create a booking
  const booking: Booking = {
    id: uuidv4(),
    teeTimeId,
    courseId,
    players: bookingPlayers,
    bookingDate: new Date(),
    confirmationCode: Math.random().toString(36).substring(2, 8).toUpperCase(),
    totalPrice,
    paid: Math.random() > 0.2, // Most bookings are paid
    additionalServices,
  };
  
  // Add to mock bookings
  mockBookings.push(booking);
  
  return booking;
};

/**
 * Gets all bookings for a specific player
 * @param playerEmail The email of the player
 * @returns An array of bookings for the player
 */
export const getPlayerBookings = (playerEmail: string): Booking[] => {
  return mockBookings.filter(booking => 
    booking.players.some(player => player.email === playerEmail)
  );
};

/**
 * Gets all bookings for a specific golf course
 * @param courseId The ID of the golf course
 * @returns An array of bookings for the course
 */
export const getCourseBookings = (courseId: string): Booking[] => {
  return mockBookings.filter(booking => booking.courseId === courseId);
};

/**
 * Cancels a booking by its ID
 * @param bookingId The ID of the booking to cancel
 * @returns true if cancellation was successful, false otherwise
 */
export const cancelBooking = (bookingId: string): boolean => {
  const bookingIndex = mockBookings.findIndex(b => b.id === bookingId);
  
  if (bookingIndex === -1) {
    return false; // Booking not found
  }
  
  const booking = mockBookings[bookingIndex];
  
  // Find the tee time for this booking
  for (const schedule of mockTeeTimes) {
    if (schedule.courseId !== booking.courseId) continue;
    
    const teeTime = schedule.teeTimes.find(tt => tt.id === booking.teeTimeId);
    if (teeTime) {
      // Free up the spots
      for (const player of booking.players) {
        for (const spot of teeTime.spots) {
          if (spot.isBooked && spot.player?.id === player.id) {
            spot.isBooked = false;
            spot.player = null;
            teeTime.availableSpots++;
          }
        }
      }
      
      teeTime.isFullyBooked = false;
      break;
    }
  }
  
  // Remove the booking
  mockBookings.splice(bookingIndex, 1);
  
  return true;
};
